import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Clock } from "lucide-react";
import { CambridgeSummerConfig, CambridgeSummerWeek } from "../../../data/cambridgeSummerConfig";

interface ProgramStructureProps {
    config: CambridgeSummerConfig;
}

const ProgramStructure: React.FC<ProgramStructureProps> = ({ config }) => {
    const { schedule } = config;
    const [expandedWeek, setExpandedWeek] = useState<number | null>(1);

    const toggleWeek = (week: number) => {
        setExpandedWeek((prev) => (prev === week ? null : week));
    };

    return (
        <section
            id="schedule"
            className="py-16 md:py-24 bg-cover bg-center bg-no-repeat"
            style={{
                backgroundImage: `linear-gradient(135deg, rgba(0, 60, 113, 0.97) 0%, rgba(0, 102, 204, 0.95) 100%), url("/images/intro-bg.webp")`,
            }}
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mb-12"
                >
                    <h2 className="text-2xl md:text-4xl font-black text-white mb-4">
                        {schedule.headline}
                    </h2>
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <Clock className="w-6 h-6 text-[#9FCEF5]" />
                        <p className="text-xl text-[#9FCEF5] font-bold">
                            {schedule.totalInstruction}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-4 text-gray-300">
                        {schedule.sessions.map((session, index) => (
                            <span key={index} className="text-base">
                                {session}
                            </span>
                        ))}
                    </div>
                </motion.div>

                {/* 2-Week Program */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="mb-16"
                >
                    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 md:p-8 mb-6">
                        <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
                            {schedule.twoWeekProgram.title}
                        </h3>
                    </div>

                    <div className="space-y-4">
                        {schedule.twoWeekProgram.weeks.map((weekItem: CambridgeSummerWeek, index: number) => {
                            const isExpanded = expandedWeek === weekItem.week;
                            return (
                                <motion.div
                                    key={weekItem.week}
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.05 }}
                                    viewport={{ once: true }}
                                    className="relative"
                                >
                                    <div
                                        className={`bg-white/10 backdrop-blur-lg rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                                            isExpanded
                                                ? "border-[#9FCEF5]"
                                                : "border-white/20 hover:border-[#9FCEF5]/50"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleWeek(weekItem.week)}
                                            className="w-full px-6 py-5 flex items-start justify-between hover:bg-white/5 transition-colors"
                                        >
                                            <div className="flex items-start gap-4 flex-1">
                                                <div
                                                    className={`md:w-14 md:h-14 w-10 h-10 rounded-xl flex items-center justify-center font-black md:text-xl text-base transition-colors flex-shrink-0 ${
                                                        isExpanded
                                                            ? "bg-[#8B1538] text-white"
                                                            : "bg-[#003C71] text-white"
                                                    }`}
                                                >
                                                    W{weekItem.week}
                                                </div>
                                                <div className="text-left flex-1 min-w-0">
                                                    <h3 className="text-[15px] font-bold text-white">
                                                        {weekItem.title}
                                                    </h3>
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {weekItem.focusAreas.length} focus areas
                                                    </p>
                                                </div>
                                            </div>
                                            <ChevronDown
                                                className={`w-5 h-5 md:w-6 md:h-6 text-gray-400 transition-transform duration-300 flex-shrink-0 mt-1 ml-2 ${
                                                    isExpanded ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-6 pb-6 pt-2 border-t border-white/10">
                                                        <div className="bg-white/5 rounded-xl p-5">
                                                            <p className="text-gray-300 mb-4">{weekItem.description}</p>
                                                            <ul className="space-y-3">
                                                                {weekItem.focusAreas.map((area: string, areaIndex: number) => (
                                                                    <li
                                                                        key={areaIndex}
                                                                        className="flex items-start gap-3 text-gray-300"
                                                                    >
                                                                        <span className="w-2 h-2 rounded-full bg-[#9FCEF5] mt-2 flex-shrink-0" />
                                                                        <span className="text-base leading-relaxed">
                                                                            {area}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                            {weekItem.includes && weekItem.includes.length > 0 && (
                                                                <div className="mt-4 pt-4 border-t border-white/10">
                                                                    <p className="text-[#9FCEF5] font-semibold mb-2">Includes:</p>
                                                                    <ul className="space-y-2">
                                                                        {weekItem.includes.map((item: string, itemIndex: number) => (
                                                                            <li key={itemIndex} className="text-gray-300 text-sm">
                                                                                • {item}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* 4-Week Program */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 md:p-8 mb-6">
                        <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
                            {schedule.fourWeekProgram.title}
                        </h3>
                    </div>

                    <div className="space-y-4 mb-8">
                        {schedule.fourWeekProgram.weeks.map((weekItem: CambridgeSummerWeek, index: number) => {
                            const weekNum = weekItem.week + 100; // Add 100 to differentiate from 2-week program
                            const isExpanded = expandedWeek === weekNum;
                            return (
                                <motion.div
                                    key={weekNum}
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.05 }}
                                    viewport={{ once: true }}
                                    className="relative"
                                >
                                    <div
                                        className={`bg-white/10 backdrop-blur-lg rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                                            isExpanded
                                                ? "border-[#9FCEF5]"
                                                : "border-white/20 hover:border-[#9FCEF5]/50"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleWeek(weekNum)}
                                            className="w-full px-6 py-5 flex items-start justify-between hover:bg-white/5 transition-colors"
                                        >
                                            <div className="flex items-start gap-4 flex-1">
                                                <div
                                                    className={`md:w-14 md:h-14 w-10 h-10 rounded-xl flex items-center justify-center font-black md:text-xl text-base transition-colors flex-shrink-0 ${
                                                        isExpanded
                                                            ? "bg-[#8B1538] text-white"
                                                            : "bg-[#003C71] text-white"
                                                    }`}
                                                >
                                                    W{weekItem.week}
                                                </div>
                                                <div className="text-left flex-1 min-w-0">
                                                    <h3 className="text-[15px] font-bold text-white">
                                                        {weekItem.title}
                                                    </h3>
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {weekItem.focusAreas.length} focus areas
                                                    </p>
                                                </div>
                                            </div>
                                            <ChevronDown
                                                className={`w-5 h-5 md:w-6 md:h-6 text-gray-400 transition-transform duration-300 flex-shrink-0 mt-1 ml-2 ${
                                                    isExpanded ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-6 pb-6 pt-2 border-t border-white/10">
                                                        <div className="bg-white/5 rounded-xl p-5">
                                                            <p className="text-gray-300 mb-4">{weekItem.description}</p>
                                                            <ul className="space-y-3">
                                                                {weekItem.focusAreas.map((area: string, areaIndex: number) => (
                                                                    <li
                                                                        key={areaIndex}
                                                                        className="flex items-start gap-3 text-gray-300"
                                                                    >
                                                                        <span className="w-2 h-2 rounded-full bg-[#9FCEF5] mt-2 flex-shrink-0" />
                                                                        <span className="text-base leading-relaxed">
                                                                            {area}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                            {weekItem.includes && weekItem.includes.length > 0 && (
                                                                <div className="mt-4 pt-4 border-t border-white/10">
                                                                    <p className="text-[#9FCEF5] font-semibold mb-2">Includes:</p>
                                                                    <ul className="space-y-2">
                                                                        {weekItem.includes.map((item: string, itemIndex: number) => (
                                                                            <li key={itemIndex} className="text-gray-300 text-sm">
                                                                                • {item}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Cultural Trips */}
                    {schedule.fourWeekProgram.culturalTrips && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 md:p-8 border-2 border-[#9FCEF5]/50"
                        >
                            <h4 className="text-xl md:text-2xl font-bold text-white mb-4 text-center">
                                Cultural & Educational Weekend Day Trips (4 Weekends)
                            </h4>
                            <ul className="space-y-3">
                                {schedule.fourWeekProgram.culturalTrips.map((trip: string, index: number) => (
                                    <li key={index} className="flex items-start gap-3 text-gray-300">
                                        <span className="text-[#9FCEF5] font-bold mt-1">{index + 1}.</span>
                                        <span className="text-base leading-relaxed flex-1">{trip}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    )}
                </motion.div>
            </div>
        </section>
    );
};

export default ProgramStructure;

